import React, { DataTestAttributes, PropsWithChildren, useState } from 'react';

import { Footer } from '@/components';
import { Breadcrumbs } from '@/components/breadcrumbs/Breadcrumbs';
import { SignedInNavbar } from '@/components/SignedInNavbar';
import { MenuItemProps } from '@/components/SignedInNavbar/Menu';
import { NavBar, SecondaryNavProps } from '@/components/widgets/o/NavBar/NavBar';
import { useIsConservation } from '@/hooks/useIsConservation';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useScreenSize } from '@/hooks/useScreenSize';
import { CustomizableWidgetTemplateLayout } from '@/layout/customizable-widget-template/CustomizableWidgetTemplate.layout';

import { CustomizableWidgetTemplateLayoutProps } from '../customizable-widget-template/constants';
import { SignedInPageLayoutContentContainer } from './components';

export type SignedInPageProps = {
  items: MenuItemProps[];
  withCustomContentContainer?: boolean;
  navBarProps?: SecondaryNavProps;
  className?: string;
  showBreadcrumbs?: boolean;
  customizableWidgetTemplateProps?: CustomizableWidgetTemplateLayoutProps;
} & DataTestAttributes;

export const SignedInPageLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer = false,
  navBarProps,
  showBreadcrumbs = true,
  ...props
}) => {
  const isStaff = useIsStaffUser();

  if (isStaff) {
    return (
      <DefaultLayout
        withCustomContentContainer={withCustomContentContainer}
        navBarProps={navBarProps}
        showBreadcrumbs={showBreadcrumbs}
        {...props}
      >
        {children}
      </DefaultLayout>
    );
  }

  return (
    <PageLayout
      withCustomContentContainer={withCustomContentContainer}
      navBarProps={navBarProps}
      showBreadcrumbs={showBreadcrumbs}
      {...props}
    >
      {children}
    </PageLayout>
  );
};

const PageLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer = false,
  navBarProps,
  ...props
}) => {
  const isConservation = useIsConservation();

  if (isConservation) {
    return (
      <ConservationLayout withCustomContentContainer={withCustomContentContainer} navBarProps={navBarProps} {...props}>
        {children}
      </ConservationLayout>
    );
  }

  return (
    <DefaultLayout withCustomContentContainer={withCustomContentContainer} navBarProps={navBarProps} {...props}>
      {children}
    </DefaultLayout>
  );
};

const DefaultLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer,
  navBarProps,
  showBreadcrumbs = true,
  className,
  customizableWidgetTemplateProps,
  'data-testid': dataTestId = 'page-container',
}) => {
  const [isCustomizing, setIsCustomizing] = useState(false);
  if (isCustomizing && customizableWidgetTemplateProps) {
    return (
      <CustomizableWidgetTemplateLayout
        {...customizableWidgetTemplateProps}
        onSuccess={() => setIsCustomizing(false)}
        onCancel={() => setIsCustomizing(false)}
      />
    );
  }

  const onGoToCustomizeLayout = () => setIsCustomizing(true);
  const enhancedNavBarProps =
    customizableWidgetTemplateProps && navBarProps ? { ...navBarProps, onGoToCustomizeLayout } : navBarProps;

  return (
    <div data-testid={dataTestId} className={className}>
      {showBreadcrumbs && <Breadcrumbs />}
      {withCustomContentContainer ? (
        <>
          {enhancedNavBarProps && <NavBar className='px-10 pt-6' {...enhancedNavBarProps} />}
          {children}
        </>
      ) : (
        <SignedInPageLayoutContentContainer>
          {enhancedNavBarProps && <NavBar {...enhancedNavBarProps} />}
          {children}
        </SignedInPageLayoutContentContainer>
      )}
    </div>
  );
};

const ConservationLayout: React.FC<PropsWithChildren<SignedInPageProps>> = ({
  children,
  withCustomContentContainer = false,
  items,
}) => {
  const isLargeScreen = useScreenSize() === 'large';

  return (
    <div className='flex flex-col h-full'>
      <SignedInNavbar items={items} />
      <div className='w-full flex-1 bg-[#F5F5F3] ' data-testid='page-container'>
        {withCustomContentContainer ? (
          children
        ) : (
          <SignedInPageLayoutContentContainer>{children}</SignedInPageLayoutContentContainer>
        )}
      </div>

      {isLargeScreen && <Footer />}
    </div>
  );
};
